import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getComunidad, getIncidencias, getAnuncios } from '../services/api';
import { Skeleton } from '../components/Toast';

function ComunidadDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comunidad, setComunidad] = useState(null);
  const [incidencias, setIncidencias] = useState([]);
  const [anuncios, setAnuncios] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarDatos();
  }, [id]);

  const cargarDatos = async () => {
    try {
      const [comRes, incRes, anunciosRes] = await Promise.all([
        getComunidad(id),
        getIncidencias({ comunidad_id: id }),
        getAnuncios({ comunidad_id: id })
      ]);

      setComunidad(comRes.data);
      setIncidencias(incRes.data);
      setAnuncios(anunciosRes.data);

      // Calcular estadísticas
      const pendientes = incRes.data.filter(i => i.estado === 'pendiente').length;
      const enProceso = incRes.data.filter(i => i.estado === 'en_proceso').length;
      const resueltas = incRes.data.filter(i => i.estado === 'resuelta').length;

      setStats({
        total: incRes.data.length,
        pendientes,
        enProceso,
        resueltas
      });
    } catch (error) {
      console.error('Error:', error);
      navigate('/comunidades');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <Skeleton height="40px" style={{ marginBottom: '20px' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px' }}>
          {[1, 2, 3].map(i => <Skeleton key={i} height="100px" />)}
        </div>
        <Skeleton height="300px" />
      </div>
    );
  }

  if (!comunidad) return null;

  const maxStat = Math.max(stats.pendientes, stats.enProceso, stats.resueltas, 1);

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <button onClick={() => navigate('/comunidades')} className="btn btn-outline btn-sm" style={{ marginBottom: '20px' }}>
        ← Volver a Comunidades
      </button>

      {/* Información de la comunidad */}
      <div className="card" style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
              🏢 {comunidad.nombre}
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '16px', marginBottom: '16px' }}>
              📍 {comunidad.direccion}, {comunidad.ciudad} {comunidad.codigo_postal}
            </p>
            <div style={{ display: 'flex', gap: '24px', fontSize: '14px' }}>
              <div>
                <strong>Viviendas:</strong> {comunidad.num_pisos || comunidad.num_viviendas || 'N/A'}
              </div>
              <div>
                <strong>Vecinos:</strong> {comunidad.num_vecinos || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Estadísticas de distribución de estados */}
      <div className="card" style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>
          📊 Distribución de Estados de Incidencias
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[
            { icon: '⏳', label: 'Pendientes', value: stats.pendientes },
            { icon: '🔧', label: 'En Proceso', value: stats.enProceso },
            { icon: '✅', label: 'Resueltas', value: stats.resueltas },
          ].map(({ icon, label, value }) => (
            <div key={label} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
              borderRadius: '14px',
            }}>
              <span style={{ fontSize: '20px' }}>{icon}</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.75)', flex: 1, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#ffffff', lineHeight: 1 }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {/* Incidencias recientes */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700' }}>📝 Incidencias Recientes</h2>
            <span className="badge">{incidencias.length} total</span>
          </div>
          {incidencias.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '20px' }}>
              No hay incidencias registradas
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {incidencias.slice(0, 5).map(inc => (
                <Link
                  key={inc.id}
                  to={`/incidencias/${inc.id}`}
                  style={{
                    textDecoration: 'none',
                    padding: '10px 12px',
                    background: 'var(--bg-elevated)',
                    borderRadius: '10px',
                    border: '1px solid var(--border-color)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.borderColor = 'var(--primary-color)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--bg-elevated)';
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '13px', lineHeight: '1.3' }}>
                      {inc.titulo}
                    </span>
                    <span className={`badge badge-${inc.estado}`} style={{ fontSize: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {inc.estado.replace('_', ' ')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                    <span>{inc.categoria}</span>
                    <span>•</span>
                    <span>{new Date(inc.fecha_creacion).toLocaleDateString('es-ES')}</span>
                  </div>
                </Link>
              ))}
              {incidencias.length > 5 && (
                <Link to="/incidencias" style={{ textAlign: 'center', fontSize: '12px', color: 'var(--primary-color)', padding: '6px', textDecoration: 'none', fontWeight: '600' }}>
                  Ver {incidencias.length - 5} más →
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Anuncios recientes */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700' }}>📢 Anuncios del Tablón</h2>
            <span className="badge">{anuncios.length} total</span>
          </div>
          {anuncios.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '20px' }}>
              No hay anuncios publicados
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {anuncios.slice(0, 5).map(anuncio => {
                const getTipoIcon = (tipo) => {
                  const icons = { nota: '📌', comunicado: '📢', acta: '📋', convocatoria: '📅' };
                  return icons[tipo] || '📄';
                };
                return (
                  <Link
                    key={anuncio.id}
                    to={`/tablon/${anuncio.id}`}
                    style={{
                      textDecoration: 'none',
                      padding: '10px 12px',
                      background: 'rgba(251,191,36,0.08)',
                      borderRadius: '10px',
                      border: '1px solid rgba(251,191,36,0.25)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(251,191,36,0.15)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(251,191,36,0.08)'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '16px' }}>{getTipoIcon(anuncio.tipo)}</span>
                      <span style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '13px', lineHeight: '1.3' }}>
                        {anuncio.titulo}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', paddingLeft: '24px' }}>
                      {new Date(anuncio.fecha_creacion).toLocaleDateString('es-ES')}
                    </div>
                  </Link>
                );
              })}
              {anuncios.length > 5 && (
                <Link to="/tablon" style={{ textAlign: 'center', fontSize: '12px', color: 'var(--primary-color)', padding: '6px', textDecoration: 'none', fontWeight: '600' }}>
                  Ver {anuncios.length - 5} más →
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ComunidadDetalle;
